/**
 * Pro 2 slimmer: the "Slimmer Than Pro / The Thinnest" section from viture.com/pro2 (its "s15").
 * Scales the design stage like the source and reveals the glow, glasses and copy once the section
 * reaches 78% down the viewport. A 30-frame glasses turn is scrubbed by scroll over the first quarter
 * screen after the section reaches the top. Frame window, scrub lag and timings are from the source.
 */
if (!customElements.get('pro2-slim')) {
  const MOBILE_QUERY = '(max-width: 640px)';
  /** @type {{ desktop: [number, number], mobile: [number, number] }} Design stage [width, height] */
  const STAGES = { desktop: [1920, 1080], mobile: [375, 812] };
  // Scroll distance, as a share of the viewport height, that turns the glasses through every frame
  const SCRUB_VH = 0.25;
  // Time constant (ms) of the lag behind the scroll position, matching the source's scrub: 0.6
  const SMOOTHING_MS = 200;
  const MAX_LOADS = 4;

  /** @typedef {HTMLImageElement & { loaded?: boolean }} FrameImage A frame, flagged once it can be drawn */
  /** @typedef {HTMLCanvasElement & { drawn?: number }} FrameCanvas A canvas, tagged with the frame it shows */

  /**
   * State set up by connectedCallback. Without a constructor, checkJs types these fields as possibly
   * undefined, so the methods declare `this` as ConnectedSlim to read them as present.
   * @typedef {object} SlimState
   * @property {MediaQueryList} mobileQuery
   * @property {boolean} reducedMotion
   * @property {string[]} urls
   * @property {FrameCanvas[]} canvases
   * @property {number} last Index of the last frame
   * @property {(FrameImage | undefined)[]} frames
   * @property {boolean} loading
   * @property {number} time Where the turn is drawn now (0–1), easing toward `target`
   * @property {number} target Where the scroll position says the turn should be (0–1)
   * @property {() => void} onResize
   * @property {() => void} onScroll
   * @property {FrameRequestCallback} tick Eases `time` toward `target`, stopping once it catches up
   * @property {number} resizeFrame
   * @property {number} generation Bumped on unload so loads still in flight are ignored
   * @property {IntersectionObserver} revealObserver Not created when reduced motion is on
   */

  /** @typedef {Pro2Slim & SlimState} ConnectedSlim */

  /**
   * @param {number} value
   * @returns {number}
   */
  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  class Pro2Slim extends HTMLElement {
    /** @this {ConnectedSlim} */
    connectedCallback() {
      /** @type {MediaQueryList} */
      this.mobileQuery = window.matchMedia(MOBILE_QUERY);
      /** @type {boolean} */
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /** @type {string[]} */
      this.urls = (this.dataset.frames || '').split(',').filter(Boolean);
      /** @type {FrameCanvas[]} */
      this.canvases = Array.from(this.querySelectorAll('canvas'));
      /** @type {number} */
      this.last = this.urls.length - 1;
      /** @type {(FrameImage | undefined)[]} */
      this.frames = [];
      /** @type {boolean} */
      this.loading = false;
      /** @type {number} */
      this.time = 0;
      /** @type {number} */
      this.target = 0;

      /** @type {() => void} */
      this.onResize = () => {
        cancelAnimationFrame(this.resizeFrame);
        /** @type {number} */
        this.resizeFrame = requestAnimationFrame(() => {
          this.#fit();
          this.#sizeCanvases();
          this.onScroll();
        });
      };
      /** @type {() => void} */
      this.onScroll = () => {
        this.#update();
        this.target = this.#scrollTime();
        if (!this.raf) this.raf = requestAnimationFrame(this.tick);
      };
      /** @type {FrameRequestCallback} */
      this.tick = (now) => {
        const elapsed = Math.min(now - (this.lastTick ?? now), 64);
        /** @type {number | null} */
        this.lastTick = now;
        this.time += (this.target - this.time) * (1 - Math.exp(-elapsed / SMOOTHING_MS));
        if (Math.abs(this.target - this.time) < 0.0005) this.time = this.target;
        this.#draw();
        if (this.time === this.target) {
          /** @type {number | null} */
          this.raf = null;
          this.lastTick = null;
        } else {
          this.raf = requestAnimationFrame(this.tick);
        }
      };

      this.#fit();
      this.#sizeCanvases();
      window.addEventListener('resize', this.onResize);
      this.mobileQuery.addEventListener('change', this.onResize);

      // Reduced motion shows the glasses fully turned, with no reveal
      if (this.reducedMotion) {
        this.urls = this.urls.slice(-1);
        this.last = 0;
        this.#load();
        return;
      }

      this.classList.add('is-armed');
      /** @type {IntersectionObserver} */
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.classList.add('is-revealed');
          this.revealObserver.disconnect();
        },
        { rootMargin: '0px 0px -22% 0px' }
      );
      this.revealObserver.observe(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.time = this.target = this.#scrollTime();
      this.onScroll();
    }

    /** @this {ConnectedSlim} */
    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.mobileQuery.removeEventListener('change', this.onResize);
      this.revealObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
      this.#unload();
    }

    /**
     * 0 while the section top is below the viewport top, 1 a quarter screen later
     * @this {ConnectedSlim}
     * @returns {number}
     */
    #scrollTime() {
      return clamp01(-this.getBoundingClientRect().top / (SCRUB_VH * window.innerHeight));
    }

    /**
     * Desktop covers the viewport; mobile is 375 wide and as tall as the viewport at that width
     * @this {ConnectedSlim}
     */
    #fit() {
      const width = window.innerWidth || this.clientWidth;
      const height = this.clientHeight;
      if (this.mobileQuery.matches) {
        const scale = width / STAGES.mobile[0];
        this.style.setProperty('--stage-h', (height / scale).toFixed(2));
        this.style.setProperty('--scale', scale.toFixed(4));
      } else {
        const [stageWidth, stageHeight] = STAGES.desktop;
        this.style.removeProperty('--stage-h');
        this.style.setProperty('--scale', Math.max(width / stageWidth, height / stageHeight).toFixed(4));
      }
    }

    /**
     * Draw at the viewport's pixel width, between 768 and the frames' 1920
     * @this {ConnectedSlim}
     */
    #sizeCanvases() {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.min(1920, Math.max(768, Math.round(window.innerWidth * pixelRatio)));
      const height = Math.round((width / 1920) * 1080);
      this.canvases.forEach((canvas) => {
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
        canvas.drawn = -1;
      });
      this.#draw();
    }

    /**
     * Draw the current frame on whichever canvas is showing
     * @this {ConnectedSlim}
     */
    #draw() {
      const frame = Math.round(this.last * this.time);
      const image = this.frames[frame];
      if (!image?.loaded) return;
      this.canvases.forEach((canvas) => {
        if (canvas.offsetParent === null || canvas.drawn === frame) return;
        const context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.drawn = frame;
      });
    }

    /**
     * Load frames within a screen of the viewport, four at a time; free them beyond two screens
     * @this {ConnectedSlim}
     */
    #update() {
      const viewportHeight = window.innerHeight;
      const rect = this.getBoundingClientRect();
      if (rect.bottom > -viewportHeight && rect.top < 2 * viewportHeight) this.#load();
      else if (rect.bottom < -2 * viewportHeight || rect.top > 3 * viewportHeight) this.#unload();
    }

    /** @this {ConnectedSlim} */
    #load() {
      if (this.loading) return;
      this.loading = true;
      const generation = (this.generation = (this.generation || 0) + 1);
      let nextIndex = 0;
      let activeLoads = 0;
      // Start loads until MAX_LOADS are in flight; each one that finishes starts the next
      const loadMore = () => {
        while (this.loading && generation === this.generation && activeLoads < MAX_LOADS && nextIndex < this.urls.length) {
          const index = nextIndex++;
          /** @type {FrameImage} */
          const image = new Image();
          image.decoding = 'async';
          this.frames[index] = image;
          activeLoads++;
          /** @param {boolean} succeeded */
          const finish = (succeeded) => {
            activeLoads--;
            if (generation !== this.generation) return;
            image.loaded = succeeded;
            if (succeeded) this.#draw();
            loadMore();
          };
          image.addEventListener('load', () => image.decode().then(() => finish(true), () => finish(image.naturalWidth > 0)), { once: true });
          image.addEventListener('error', () => finish(false), { once: true });
          image.src = /** @type {string} */ (this.urls[index]);
        }
      };
      loadMore();
    }

    /** @this {ConnectedSlim} */
    #unload() {
      if (!this.loading) return;
      this.loading = false;
      this.generation++;
      this.frames.forEach((image) => image && (image.src = ''));
      this.frames = [];
      this.canvases.forEach((canvas) => (canvas.drawn = -1));
    }
  }

  customElements.define('pro2-slim', Pro2Slim);
}
